import type { UseQueryResult } from '@tanstack/react-query';
import type { CleaningTaskQueueResponse } from '@/shared/api';
import { problemText } from '@/shared/api/problemText';
import { useApiProblem } from '@/shared/api/useApiProblem';
import { Button } from '@/shared/components';
import {
  SurfaceLoadError,
  SurfaceLoading,
  SurfaceStaleHint,
} from '@/shared/components/SurfaceStates';
import { FoldSection } from './FoldSection';
import { buildDayPlan } from './myTasksModel';
import { NowCard } from './NowCard';
import { PlanFailureBanner } from './PlanFailureBanner';
import { PlanEntry } from './PlanEntry';
import type { StepVars } from './useTaskStep';

const CARD = 'border-hairline rounded-tile overflow-hidden border bg-white';

export interface StepFailure {
  id: string;
  name: string;
  message: string;
}

/** Der Tagesplan mit seinen Zuständen: lädt, keine Verbindung, Fehler mit Grund, veraltet, leer. */
export function MyDayList({
  query,
  dayLabel,
  today,
  userId,
  timeZone,
  online,
  busyId,
  failure,
  onDismissFailure,
  onStep,
  onNextDay,
}: {
  query: UseQueryResult<CleaningTaskQueueResponse[]>;
  /** „Heute“, „Morgen“ oder das Datum — für den Satz im leeren Plan. */
  dayLabel: string;
  today: string;
  userId: string | undefined;
  timeZone?: string;
  online: boolean;
  busyId: string | undefined;
  failure: StepFailure | null;
  onDismissFailure: () => void;
  onStep: (vars: StepVars) => void;
  onNextDay: () => void;
}) {
  const problem = useApiProblem(query.error);
  const retry = () => void query.refetch();
  if (query.isPending && query.fetchStatus === 'paused')
    return (
      <p role="status" className={`${CARD} text-ink-soft p-5 text-sm`}>
        Keine Verbindung. Der Tagesplan lädt, sobald sie zurück ist.
      </p>
    );
  if (query.isPending)
    return (
      <div className={CARD}>
        <SurfaceLoading label="Tagesplan wird geladen" rows={3} />
      </div>
    );
  if (!query.data)
    return (
      <div className={CARD}>
        <SurfaceLoadError
          message={problemText(problem, 'Der Tagesplan konnte nicht geladen werden.')}
          onRetry={retry}
          retrying={query.isFetching}
        />
      </div>
    );

  const plan = buildDayPlan(query.data, userId);
  const nothingOpen = !plan.current && plan.upcoming.length === 0;
  const failureHere = (id: string) => (failure?.id === id ? failure.message : null);
  const failureElsewhere =
    failure &&
    !plan.upcoming.some((task) => task.id === failure.id) &&
    plan.current?.id !== failure.id;
  return (
    <div className="flex flex-col gap-3">
      {query.isError && (
        <div className={CARD}>
          <SurfaceStaleHint onRetry={retry} retrying={query.isFetching} />
        </div>
      )}
      {failureElsewhere && (
        <PlanFailureBanner
          name={failure.name}
          message={failure.message}
          onDismiss={onDismissFailure}
        />
      )}
      {plan.current && (
        <NowCard
          task={plan.current}
          today={today}
          timeZone={timeZone}
          failure={failureHere(plan.current.id)}
        />
      )}
      {plan.upcoming.length > 0 && (
        <ol aria-label="Zeitleiste" className="flex flex-col">
          {plan.upcoming.map((task) => (
            <PlanEntry
              key={task.id}
              task={task}
              userId={userId}
              today={today}
              busy={busyId === task.id}
              locked={!online || busyId !== undefined}
              failure={failureHere(task.id)}
              onStep={(step) => onStep({ task, step })}
            />
          ))}
        </ol>
      )}
      {nothingOpen && (
        <section className={`${CARD} flex flex-col gap-2 p-4`}>
          <b className="text-[15px]">
            {plan.done.length > 0
              ? `${dayLabel}: alles erledigt`
              : plan.cancelled.length > 0
                ? `${dayLabel}: nichts offen`
                : `${dayLabel}: keine Aufgaben`}
          </b>
          <p className="text-ink-soft text-sm">
            {plan.done.length + plan.cancelled.length > 0
              ? 'Für dich ist nichts mehr offen, und keine Aufgabe ist frei.'
              : 'Für dich ist nichts geplant, und keine Aufgabe ist frei.'}
          </p>
          <Button variant="secondary" size="lg" className="self-start" onClick={onNextDay}>
            Nächsten Tag ansehen
          </Button>
        </section>
      )}
      <FoldSection title="Erledigt" tasks={plan.done} userId={userId} timeZone={timeZone} />
      <FoldSection title="Entfällt" tasks={plan.cancelled} userId={userId} timeZone={timeZone} />
      <FoldSection title="Bei anderen" tasks={plan.others} userId={userId} timeZone={timeZone} />
    </div>
  );
}
